import React, { useCallback, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import ReactDatePicker from 'react-datepicker'
import { useIntl } from 'react-intl'
import MultilineTextEditor from '../../../../../../../../../components/editors/multiline-text/MultilineTextEditor'
import { MarkdownPreview } from '../../../../../../../../../components/editors/markdown/Markdown/MarkdownPreview'
import '../../../../../../../../../components/editors/markdown/Markdown/styles.scss'
import { Button } from '../../../../../../../../../components/buttons'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import { colorPalette } from '../../../../../../../../../style/colorPalette'
import { LINK_COLOR_PRESETS } from '../constants'
import { dateV2Service } from '../../../../../../../../../../domain/value-object/DateV2'

export type LinkEditorResult = {
  color?: string
  description?: string
  fromDate?: CalendarDateVO
  toDate?: CalendarDateVO
}

type LinkEditorPopupProps = {
  title: string
  isCriticalPath: boolean
  fromName: string
  toName: string
  fromIconUrl?: string
  toIconUrl?: string
  // プロジェクト名・アイコン（プログラム側CPで表示）
  fromProjectName?: string
  toProjectName?: string
  fromProjectIconUrl?: string
  toProjectIconUrl?: string
  // Date-related (critical path only)
  fromDate?: CalendarDateVO
  toDate?: CalendarDateVO
  fromDateRange?: { min: Date; max: Date } | null
  toDateRange?: { min: Date; max: Date } | null
  // Color & description
  initialColor?: string
  initialDescription?: string
  // Callbacks
  onSave: (updates: LinkEditorResult) => void
  onDelete: () => void
  onClose: () => void
  // Read-only mode (disables all editing controls)
  readOnly?: boolean
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: 620,
    maxHeight: '80vh',
  },
})

const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  paddingBottom: 16,
})

const TitleContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const StyledDialogContent = styled(DialogContent)({
  paddingTop: 24,
  overflow: 'auto',
})

const FormContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  paddingTop: 8,
})

const FieldLabel = styled(Typography)({
  marginBottom: 4,
  display: 'block',
})

const DetailWbsItemRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 2,
})

const WbsItemIcon = styled('img')({
  width: 16,
  height: 16,
  flexShrink: 0,
})
const ProjectIcon = styled('img')({
  width: 16,
  height: 16,
  flexShrink: 0,
  borderRadius: '50%',
  objectFit: 'cover',
})
const ProjectNameText = styled('span')({
  fontSize: 11,
  color: colorPalette.monotone[4],
  whiteSpace: 'nowrap',
  marginLeft: 6,
  flexShrink: 0,
})

const DatePickerWrapper = styled('div')({
  marginTop: 4,
  '& .react-datepicker-wrapper': {
    width: '100%',
  },
  '& input': {
    width: '100%',
    padding: '6px 8px',
    border: `1px solid ${colorPalette.monotone[2]}`,
    borderRadius: 4,
    fontSize: 14,
    outline: 'none',
    '&:focus': {
      borderColor: colorPalette.skyBlue[5],
    },
  },
})

const ColorCircle = styled('div')<{ bgcolor: string }>(({ bgcolor }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: bgcolor,
}))

const DescriptionContainer = styled('div')({
  height: 250,
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: 4,
  overflow: 'hidden',
})

const StyledDialogActions = styled(DialogActions)({
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
  paddingLeft: 24,
  paddingRight: 24,
  paddingTop: 16,
  paddingBottom: 16,
  justifyContent: 'space-between',
})

const DeleteButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
})

const RightActions = styled('div')({
  display: 'flex',
  gap: 8,
})

const ReadOnlyText = styled(Typography)({
  marginTop: 4,
})

const MarkdownContainer = styled('div')({
  fontSize: 14,
})

// ============================================================================
// Field Components
// ============================================================================

type LinkDateFieldProps = {
  date: CalendarDateVO
  localDate?: CalendarDateVO
  onChange: (date: Date | null) => void
  dateRange?: { min: Date; max: Date } | null
  readOnly?: boolean
}

const LinkDateField = ({
  date,
  localDate,
  onChange,
  dateRange,
  readOnly,
}: LinkDateFieldProps) => {
  if (readOnly) {
    return (
      <ReadOnlyText variant="body2">
        {dateV2Service.format(date.date)}
      </ReadOnlyText>
    )
  }
  return (
    <DatePickerWrapper>
      <ReactDatePicker
        selected={localDate?.date}
        onChange={onChange}
        dateFormat="yyyy/MM/dd"
        placeholderText="YYYY/MM/DD"
        minDate={dateRange?.min}
        maxDate={dateRange?.max}
      />
    </DatePickerWrapper>
  )
}

type LinkColorFieldProps = {
  color: string | undefined
  onChange: (color: string) => void
  readOnly?: boolean
}

const LinkColorField = ({ color, onChange, readOnly }: LinkColorFieldProps) => {
  if (readOnly) {
    return <ColorCircle bgcolor={color ?? colorPalette.pink[5]} />
  }
  return (
    <ToggleButtonGroup
      exclusive={true}
      size="small"
      value={color ?? null}
      onChange={(_, v) => {
        if (v) onChange(v)
      }}
    >
      {LINK_COLOR_PRESETS.map(preset => (
        <ToggleButton key={preset} value={preset}>
          <ColorCircle bgcolor={preset} />
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

type LinkDescriptionFieldProps = {
  description: string
  onChange: (value: string) => void
  readOnly?: boolean
}

const LinkDescriptionField = ({
  description,
  onChange,
  readOnly,
}: LinkDescriptionFieldProps) => {
  if (readOnly) {
    if (!description) return null
    return (
      <MarkdownContainer className="markdown-body">
        <MarkdownPreview text={description} />
      </MarkdownContainer>
    )
  }
  return (
    <DescriptionContainer>
      <MultilineTextEditor
        value={description}
        setValue={onChange}
        disableSbsMode={true}
      />
    </DescriptionContainer>
  )
}

// ============================================================================
// Component
// ============================================================================

export const LinkEditorPopup: React.FC<LinkEditorPopupProps> = ({
  title,
  isCriticalPath,
  fromName,
  toName,
  fromIconUrl,
  toIconUrl,
  fromProjectName,
  toProjectName,
  fromProjectIconUrl,
  toProjectIconUrl,
  fromDate,
  toDate,
  fromDateRange,
  toDateRange,
  initialColor,
  initialDescription,
  onSave,
  onDelete,
  onClose,
  readOnly,
}) => {
  const intl = useIntl()

  // Local buffered state
  const [color, setColor] = useState<string | undefined>(initialColor)
  const [description, setDescription] = useState(initialDescription ?? '')
  const [localFromDate, setLocalFromDate] = useState(fromDate)
  const [localToDate, setLocalToDate] = useState(toDate)

  const handleFromDateChange = useCallback((date: Date | null) => {
    if (!date) return
    setLocalFromDate(CalendarDateVO.of(date))
  }, [])

  const handleToDateChange = useCallback((date: Date | null) => {
    if (!date) return
    setLocalToDate(CalendarDateVO.of(date))
  }, [])

  const handleDescriptionChange = useCallback((value: string) => {
    setDescription(value)
  }, [])

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const handleRequestDelete = useCallback(() => {
    setDeleteConfirmOpen(true)
  }, [])

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    setDeleteConfirmOpen(false)
    onDelete()
  }, [onDelete])

  const typeLabel = intl.formatMessage({
    id: isCriticalPath
      ? 'wbsItemLink.criticalPath.dialog.title'
      : 'wbsItemLink.dependency.dialog.title',
  })

  const handleSave = useCallback(() => {
    const result: LinkEditorResult = {}
    if (color !== initialColor) {
      result.color = color
    }
    const trimmedDesc = description.trim()
    const initialDesc = (initialDescription ?? '').trim()
    if (trimmedDesc !== initialDesc) {
      result.description = trimmedDesc || undefined
    }
    if (isCriticalPath) {
      if (localFromDate && fromDate && !localFromDate.isEqual(fromDate)) {
        result.fromDate = localFromDate
      }
      if (localToDate && toDate && !localToDate.isEqual(toDate)) {
        result.toDate = localToDate
      }
    }
    onSave(result)
  }, [
    color,
    initialColor,
    description,
    initialDescription,
    isCriticalPath,
    localFromDate,
    fromDate,
    localToDate,
    toDate,
    onSave,
  ])

  return (
    <StyledDialog open={true} onClose={onClose} maxWidth={false}>
      <StyledDialogTitle>
        <TitleContent>
          <Typography variant="h6" component="span">
            {title}
          </Typography>
        </TitleContent>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <FormContainer>
          {/* From */}
          <div>
            <FieldLabel variant="caption" color="text.secondary">
              {intl.formatMessage({ id: 'wbsItemLink.detail.from' })}
            </FieldLabel>
            <DetailWbsItemRow>
              {fromProjectIconUrl && <ProjectIcon src={fromProjectIconUrl} />}
              {fromProjectName && (
                <ProjectNameText>{fromProjectName}</ProjectNameText>
              )}
              {fromIconUrl && <WbsItemIcon src={fromIconUrl} />}
              <Typography variant="body2">{fromName}</Typography>
            </DetailWbsItemRow>
            {isCriticalPath && fromDate && (
              <LinkDateField
                date={fromDate}
                localDate={localFromDate}
                onChange={handleFromDateChange}
                dateRange={fromDateRange}
                readOnly={readOnly}
              />
            )}
          </div>

          {/* To */}
          <div>
            <FieldLabel variant="caption" color="text.secondary">
              {intl.formatMessage({ id: 'wbsItemLink.detail.to' })}
            </FieldLabel>
            <DetailWbsItemRow>
              {toProjectIconUrl && <ProjectIcon src={toProjectIconUrl} />}
              {toProjectName && (
                <ProjectNameText>{toProjectName}</ProjectNameText>
              )}
              {toIconUrl && <WbsItemIcon src={toIconUrl} />}
              <Typography variant="body2">{toName}</Typography>
            </DetailWbsItemRow>
            {isCriticalPath && toDate && (
              <LinkDateField
                date={toDate}
                localDate={localToDate}
                onChange={handleToDateChange}
                dateRange={toDateRange}
                readOnly={readOnly}
              />
            )}
          </div>

          {/* Color (critical path only) */}
          {isCriticalPath && (
            <div>
              <FieldLabel variant="caption" color="text.secondary">
                {intl.formatMessage({ id: 'gantt.link.color' })}
              </FieldLabel>
              <LinkColorField
                color={color}
                onChange={setColor}
                readOnly={readOnly}
              />
            </div>
          )}

          {/* Description (critical path only) */}
          {isCriticalPath && (
            <div>
              <FieldLabel variant="caption" color="text.secondary">
                {intl.formatMessage({ id: 'gantt.link.description' })}
              </FieldLabel>
              <LinkDescriptionField
                description={description}
                onChange={handleDescriptionChange}
                readOnly={readOnly}
              />
            </div>
          )}
        </FormContainer>
      </StyledDialogContent>

      <StyledDialogActions>
        {readOnly ? (
          <RightActions>
            <Button
              colorPattern="monotone"
              variant="outlined"
              onClick={onClose}
            >
              {intl.formatMessage({ id: 'dialog.close' })}
            </Button>
          </RightActions>
        ) : (
          <>
            <DeleteButton
              colorPattern="pink"
              variant="outlined"
              onClick={handleRequestDelete}
            >
              <DeleteRoundedIcon sx={{ fontSize: 16 }} />
              {intl.formatMessage({ id: 'dialog.delete' })}
            </DeleteButton>
            <RightActions>
              <Button
                colorPattern="monotone"
                variant="outlined"
                onClick={onClose}
              >
                {intl.formatMessage({ id: 'gantt.link.cancel' })}
              </Button>
              <Button colorPattern="skyBlue" onClick={handleSave}>
                {intl.formatMessage({ id: 'gantt.link.save' })}
              </Button>
            </RightActions>
          </>
        )}
      </StyledDialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'wbsItemLink.delete.confirm.title' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage(
              { id: 'wbsItemLink.delete.confirm.message' },
              { type: typeLabel }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            colorPattern="monotone"
            variant="outlined"
            onClick={handleCancelDelete}
          >
            {intl.formatMessage({ id: 'wbsItemLink.button.cancel' })}
          </Button>
          <Button colorPattern="pink" onClick={handleConfirmDelete}>
            {intl.formatMessage({ id: 'wbsItemLink.button.delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledDialog>
  )
}
